import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import {
  OrderStatus,
  type CreateOrderLineDTO,
  type OrderDTO,
  type OrderLineDTO,
  type UpdateOrderDTO,
} from '@invenco/sales-interface/interfaces';
import type { ShipmentDTO } from '@invenco/shipping-interface';

import { LIVE_DATA_INTERVAL_MS } from '@/constants';
import { useGateways } from '@/gateways/GatewayProvider';
import { extractErrorMessage, fetchAllPages } from '@/shared/helpers';
import {
  useEntityDetailsQuery,
  useGatewayMutation,
  useQueryWithInput,
} from '@/shared/hooks/queries';
import { useMessages } from '@/shared/providers/MessagesProvider';

import type { OrderLineAddInput, OrderLineUpdatePatch } from '../order-lines-card';
import { orderActionSuccessMessage } from './order-action-details';

function shouldRefetchLiveData(order?: OrderDTO) {
  return order?.status === OrderStatus.PENDING || order?.status === OrderStatus.OPEN;
}

const getCreateOrderLineDTO = (orderLine: OrderLineAddInput): CreateOrderLineDTO => ({
  skuName: orderLine.skuName,
  description: orderLine.description,
  qtyOrdered: orderLine.qtyOrdered,
  unitPrice: orderLine.unitPrice,
  discountAmount: orderLine.discountAmount,
  discountRate: orderLine.discountRate,
});

const EMPTY_SHIPMENTS: ShipmentDTO[] = [];

export function useOrder(id: string) {
  const { salesGateway, shippingGateway } = useGateways();
  const messages = useMessages();
  const queryClient = useQueryClient();
  const orderQueryKey = ['orders', id];

  const {
    data: order,
    isLoading,
    refetch: refetchOrder,
  } = useEntityDetailsQuery<OrderDTO>({
    parentKey: 'orders',
    id,
    query: (fetchId, { signal }) => salesGateway.getOrder(fetchId, { signal }),
    refetchInterval: ({ state }) =>
      shouldRefetchLiveData(state.data) ? LIVE_DATA_INTERVAL_MS : undefined,
  });

  const fulfillmentIds = order?.fulfillments?.map((fulfillment) => fulfillment.id) ?? [];
  const {
    data: shipmentsData,
    error: shipmentsError,
    isLoading: isLoadingShipments,
  } = useQueryWithInput({
    parentKey: 'order-timeline-shipments',
    input: { shippableIds: fulfillmentIds },
    query: (input, { signal }) =>
      fetchAllPages(({ cursor, take }) =>
        shippingGateway.getShipments({ ...input, cursor, take }, { signal }),
      ),
    enabled: fulfillmentIds.length > 0,
    refetchInterval: shouldRefetchLiveData(order) ? LIVE_DATA_INTERVAL_MS : undefined,
  });

  useEffect(() => {
    if (shipmentsError) {
      messages.error(extractErrorMessage(shipmentsError), {
        key: `order-timeline-shipments-${id}-error`,
      });
    }
  }, [shipmentsError, messages, id]);

  const updateCachedOrder = (updater: (currentOrder: OrderDTO) => OrderDTO) => {
    queryClient.setQueryData<OrderDTO>(orderQueryKey, (currentOrder) =>
      currentOrder ? updater(currentOrder) : currentOrder,
    );
  };

  const { mutate: addOrderLine } = useGatewayMutation<OrderLineDTO, OrderLineAddInput>({
    mutationFn: (line) => salesGateway.addOrderLine(id, getCreateOrderLineDTO(line)),
    onSuccess: (newLine) => {
      updateCachedOrder((currentOrder) => ({
        ...currentOrder,
        orderLines: [...(currentOrder.orderLines ?? []), newLine],
      }));
    },
    linkedQuery: orderQueryKey,
    linkResponseToQuery: false,
    successMessage: 'Added line to order',
  });

  const { mutate: updateOrderLineMutation } = useGatewayMutation<
    OrderLineDTO,
    { lineId: string; patch: OrderLineUpdatePatch }
  >({
    mutationFn: ({ lineId, patch }) => salesGateway.updateOrderLine(id, { ...patch, id: lineId }),
    onSuccess: (updatedLine) => {
      updateCachedOrder((currentOrder) => ({
        ...currentOrder,
        orderLines: (currentOrder.orderLines ?? []).map((line) =>
          line.id === updatedLine.id ? updatedLine : line,
        ),
      }));
    },
    linkedQuery: orderQueryKey,
    linkResponseToQuery: false,
    successMessage: 'Updated line',
  });

  const { mutate: updateOrder } = useGatewayMutation<OrderDTO, UpdateOrderDTO>({
    mutationFn: (patch) => salesGateway.updateOrder(id, patch),
    linkedQuery: orderQueryKey,
    successMessage: 'Updated order',
  });

  const { mutate: applyManualHold } = useGatewayMutation<OrderDTO>({
    mutationFn: () => salesGateway.applyManualHold(id),
    linkedQuery: orderQueryKey,
    successMessage: orderActionSuccessMessage.APPLY_MANUAL_HOLD,
  });

  const { mutate: releaseManualHold } = useGatewayMutation<OrderDTO>({
    mutationFn: () => salesGateway.releaseManualHold(id),
    linkedQuery: orderQueryKey,
    successMessage: orderActionSuccessMessage.RELEASE_MANUAL_HOLD,
  });

  const { mutate: revertToDraft } = useGatewayMutation<OrderDTO>({
    mutationFn: () => salesGateway.revertToDraft(id),
    linkedQuery: orderQueryKey,
    successMessage: orderActionSuccessMessage.REVERT_TO_DRAFT,
  });

  const { mutate: releaseOrder } = useGatewayMutation<OrderDTO>({
    mutationFn: () => salesGateway.releaseOrder(id),
    linkedQuery: orderQueryKey,
    successMessage: orderActionSuccessMessage.RELEASE,
  });

  const { mutate: cancelOrder } = useGatewayMutation<OrderDTO>({
    mutationFn: () => salesGateway.cancelOrder(id),
    linkedQuery: orderQueryKey,
    successMessage: orderActionSuccessMessage.CANCEL,
  });

  const { mutate: closeOrder } = useGatewayMutation<OrderDTO>({
    mutationFn: () => salesGateway.closeOrder(id),
    linkedQuery: orderQueryKey,
    successMessage: orderActionSuccessMessage.CLOSE,
  });

  const updateOrderLine = (lineId: string, patch: OrderLineUpdatePatch) =>
    updateOrderLineMutation({ lineId, patch });

  return {
    order,
    shipments: shipmentsData ?? EMPTY_SHIPMENTS,
    shipmentsError,
    isLoading,
    isLoadingShipments,
    refetchOrder,
    addOrderLine,
    updateOrder,
    updateOrderLine,
    applyManualHold,
    releaseManualHold,
    revertToDraft,
    releaseOrder,
    cancelOrder,
    closeOrder,
  };
}
